// AI Assistant — a classic chat assistant for prompt brainstorming.
// Lives BESIDE the Workbench and Commands editor. Has NO connection to the silo RAG.
// Each rendered surface uses a distinct `surface` key so its conversation persists
// independently in sessionStorage across tab switches.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Robot, PaperPlaneTilt, Trash, ArrowUp, ClipboardText, X, CaretUp, CaretDown, CaretLeft, CaretRight, SlidersHorizontal, Warning, FloppyDisk, SpeakerHigh, SpeakerSimpleHigh, Pause, Play, Stop, Waveform, DotsSixVertical, ShareNetwork, Sparkle, BookOpen, Globe } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { MODEL_GLOSSARY } from "@/lib/modelGlossary";
import { linkifyUrls } from "@/lib/linkify";
import { useVoiceQA, VOICE_OPTIONS, chunkForSpeech } from "@/lib/useVoiceQA";
import ResizeHint from "@/components/ResizeHint";
import LLMGuardWrap from "@/components/LLMGuardWrap";
import AiAssistantModelPicker from "@/components/AiAssistantModelPicker";
import SessionLlmPickerModal from "@/components/SessionLlmPickerModal";
import { overrideAsAskBody, readSessionLlmOverride, labelForModel } from "@/lib/sessionLlm";
import AiBuddyVoiceLanes from "@/components/AiBuddyVoiceLanes";
import { CurvedListenButton } from "@/components/CurvedListenButton";
import { ConversationScrubber, buildScrubStops, stopIndexFor } from "@/components/ConversationScrubber";
import ShareConversationSheet from "@/components/ShareConversationSheet";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";
import BuddyFilePicker from "@/components/BuddyFilePicker";

const fmtCents = (c) => (c == null ? "—" : c < 100 ? `${c}¢` : `$${(c / 100).toFixed(2)}`);

const STORAGE_PREFIX = "buddy_session__";

// Jun 2026 (Randy) — the session pointer lives in localStorage (not
// sessionStorage) so the AI Assistant recalls your conversation even after you
// close the tab/app and come back, the same way the Staging area does. A
// server-side latest-session lookup backs this up if local storage is gone.
function getStoredSessionId(surface) {
  try { return localStorage.getItem(STORAGE_PREFIX + surface) || ""; } catch { return ""; }
}
function setStoredSessionId(surface, sid) {
  try {
    if (sid) localStorage.setItem(STORAGE_PREFIX + surface, sid);
    else localStorage.removeItem(STORAGE_PREFIX + surface);
  } catch { /* ignore */ }
}

export const seedAssistantStorageKey = (surface) => `buddy_seed__${surface}`;

export const seedAssistant = (surface, text) => {
  if (!text) return;
  try {
    sessionStorage.setItem(seedAssistantStorageKey(surface), text);
    window.dispatchEvent(new CustomEvent("buddy-seed", { detail: { surface, length: text.length } }));
  } catch { /* ignore */ }
};

/**
 * Props:
 *   surface: string — unique key per mount location (e.g. "workbench", "commands-editor")
 *   onAppendToStaging?: (text) => void — when supplied, shows "Append to staging" pill
 *   onPasteToInstructions?: (text) => void — when supplied, shows "Paste to instructions" pill
 *   defaultOpen?: boolean (default: false)
 *   compact?: boolean — render slim header (used inside dialogs)
 */
export default function AiBuddy({
  surface = "default",
  onAppendToStaging,
  onSendToWorkbench,
  onJumpToWorkbench = null,
  onSendAllToWorkbench = null,
  onDeleteTurn = null,
  answerStyle = null,
  onPasteToInstructions,
  onCommitToSilo,
  defaultOpen = false,
  compact = false,
  siloId = null,
  siloSlug = null,
  showSiloControls = false,
  title = "AI Assistant",
  subtitle = "Brainstorm prompts & commands",
  emptyHint = "Ask the AI Assistant anything — it never reads your documents.",
  inputPlaceholder = "Ask the AI Assistant for a command, refinement, or prompt idea…",
  hideHeader = false,        // full-page mode: drop the card chrome + "Your Assistant" header
  emptyState = null,         // full-page mode: rich empty-state node (replaces emptyHint)
  fullHeight = false,        // full-page mode: taller response area
  onMessagesChange = null,   // report the live conversation up (for "Bookmark this conversation")
  clearRequest = null,       // a nonce bumped by the site-header "Clear" button (full-page)
  loadRequest = null,        // { nonce, turns:[{role,text}] } → replay a saved bookmark into the chat
  bookmarkRequest = null,    // a nonce bumped by the Tools tab to open the "name it" modal
  onBookmarkSaved = null,    // ping so the Bookmarks tab refreshes
  startFresh = false,        // full-page mode: always open blank (no auto-restore)
  siloName = "this silo",    // for the Share sheet's copy
}) {
  const sessionKey = siloId ? `${surface}::${siloId}` : surface;
  // On the private AI Assistant page, the per-answer Listen reads the QUESTION
  // first, then the ANSWER, as one clip (Randy, Jun 2026 — cost-saving groundwork).
  const combineQA = surface === "silo-assistant";
  const rootRef = useRef(null);
  const [open, setOpen] = useState(() => {
    if (hideHeader) return true;   // full-page: always open, no toggle
    if (showSiloControls) {
      // Jul 2026 (Randy) — the upgraded assistant (Workbench + Refinery) now
      // defaults MINIMIZED on desktop AND mobile; we still remember if the user
      // deliberately opens it (durable localStorage).
      try { const v = localStorage.getItem(`buddyopen:${surface}`); if (v != null) return v === "1"; } catch { /* */ }
      return false;
    }
    return defaultOpen;
  });
  const [sessionId, setSessionId] = useState(() => (startFresh ? "" : getStoredSessionId(sessionKey)));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const [modelLabel, setModelLabel] = useState(null);
  const [modelId, setModelId] = useState(null);
  const [siloContext, setSiloContext] = useState("off"); // off | full | chunks | silo | file
  // Feb 10, 2026 (Randy) — Master AI Assistant 3-button matrix. Each button
  // is INDEPENDENT (any combination is legal except "everything off"):
  //   • llmMode  — "training" | "training_grounded" | "off"
  //   • siloOn   — Silo knowledge on/off
  //   • filesOn  — Selected files on/off (opens picker when toggling on)
  // The old `siloContext` state is still used for the network payload,
  // computed from siloOn + filesOn below. Combined Silo+Files sends
  // siloContext="silo" plus silo_file_ids so the backend auto-compares.
  const [llmMode, setLlmMode] = useState("training");
  const [siloOn, setSiloOn] = useState(false);
  const [filesOn, setFilesOn] = useState(false);
  // Jul 2026 (Randy) — "File" mode: the assistant pairs the world LLM with ONLY
  // these selected files (any tier) instead of the whole silo. Remembered per silo.
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [selectedFileMeta, setSelectedFileMeta] = useState(null); // { count, chars, mode }
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Phase B gating — the AI Assistant is a Creator Pro tool. Gate the two
  // user-facing mounts (Workbench assistant + main full-page assistant) by SKU.
  // Other mounts (e.g. the command-editor helper) stay ungated.
  const { ent, owns, lockStyle } = useEntitlements();
  // Jul 2026 (Randy) — the Refinery tab now mounts the SAME upgraded assistant
  // as the Workbench (Full-Silo knowledge included), gated by the SAME
  // Workbench-Suite SKUs so one unlock covers both surfaces.
  const _wbAssistantSurface = surface === "workbench" || surface === "file-refinery";
  const _assistantSku = _wbAssistantSurface ? "wb_ai_assistant" : surface === "silo-assistant" ? "master_ai" : null;
  const _siloSku = _wbAssistantSurface ? "wb_silo_collab" : surface === "silo-assistant" ? "master_silo_collab" : null;
  const assistantLocked = !!_assistantSku && !!ent && !owns(_assistantSku);
  const siloKnowledgeLocked = !!_siloSku && !!ent && !owns(_siloSku);
  const { openGate } = useCreatorProGate();

  // Conversations are scoped per (surface + silo) so a chat in one silo never
  // bleeds into another silo's Workbench. When the silo changes, swap to that
  // silo's stored session and reload its history. If there's no local pointer
  // (closed tab / cleared cache / new device), ask the server for the latest
  // session on this surface so the conversation still comes back — server-backed
  // recall, just like the Staging area.
  useEffect(() => {
    setMessages([]);
    setHistoryLoaded(false);
    // Full-page AI Assistant (Randy, Jun 2026) — always opens to a blank, fresh
    // chat. Past conversations are reopened deliberately via the Bookmarks tab or
    // the Recall button, never auto-restored. Other mounts keep server-backed recall.
    if (startFresh) { setSessionId(""); return; }
    const local = getStoredSessionId(sessionKey);
    setSessionId(local);
    if (!local) {
      let cancel = false;
      (async () => {
        try {
          const r = await api.get(`/buddy/latest-session?surface=${encodeURIComponent(sessionKey)}`);
          const sid = r.data?.session_id;
          if (!cancel && sid) { setSessionId(sid); setStoredSessionId(sessionKey, sid); }
        } catch { /* fresh session is fine */ }
      })();
      return () => { cancel = true; };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  // Feb 10, 2026 (Randy) — Keep the legacy `siloContext` string in sync
  // with the two independent toggles (`siloOn`, `filesOn`) so the network
  // payload never needs a special case. When BOTH are on we send
  // silo_context="silo" AND silo_file_ids — the backend detects this as
  // combined-compare mode and auto-injects the compare/contrast prompt.
  useEffect(() => {
    if (siloOn && filesOn) setSiloContext("silo");
    else if (siloOn) setSiloContext("silo");
    else if (filesOn) setSiloContext("file");
    else setSiloContext("off");
  }, [siloOn, filesOn]);

  // Feb 10, 2026 (Randy pt-master-assistant-interlocks) — 3-button
  // auto-correct interlocks. Every state change goes through one of the
  // wrappers below so the assistant NEVER lands on "everything off".
  // Rules (in Randy's words):
  //   • "If files is on, force pop LLM on"
  //     → when files become active while LLM is off, flip LLM back to
  //       Training. Fires from both the toggle-on path AND the
  //       file-picker Save path.
  //   • "If they flip LLM off, pop silo on"
  //     → user tapping LLM Off with silo off flips silo on so the
  //       assistant still has a knowledge source.
  //   • "If they tap silo off, pop LLM back on"
  //     → user tapping silo off while LLM is off flips LLM back to
  //       Training so the assistant still has a source.
  const setLlmModeSafe = (newMode) => {
    if (newMode === "off" && !siloOn) {
      // "If they flip LLM off, pop silo on"
      setSiloOn(true);
    }
    setLlmMode(newMode);
  };
  const setSiloOnSafe = (nextOn) => {
    if (nextOn === false && llmMode === "off") {
      // "If they tap silo off, pop LLM back on"
      setLlmMode("training");
    }
    setSiloOn(nextOn);
  };
  const setFilesOnSafe = (nextOn) => {
    if (nextOn === true && llmMode === "off") {
      // "If file is on, force pop LLM on"
      setLlmMode("training");
    }
    setFilesOn(nextOn);
  };

  // Fresh chat = fresh knowledge state. Every new session starts with
  // Training / Silo Off / Files Off so the user never inherits a stale
  // toggle from the previous conversation.
  useEffect(() => {
    if (!sessionId) {
      setLlmMode("training");
      setSiloOn(false);
      setFilesOn(false);
      setSelectedFileIds([]);
    }
  }, [sessionId]);

  // Resolve the live model routed to the ai_assistant surface (silo-aware when a
  // siloId is supplied) so the subtitle truthfully reflects the current model.
  //
  // Iter160p (Randy pt-master-assistant-session-picker) — if the user picked a
  // session-only override, honor THAT here so the pill label reflects the
  // model the next message will actually run against. The picker fires
  // `ai-buddy-model-changed` on save/reset, so this re-runs and reflects the
  // new state without a page reload.
  const fetchEffective = useCallback(async () => {
    try {
      const ov = readSessionLlmOverride("ai_assistant");
      if (ov?.model) {
        setModelId(ov.model);
        setModelLabel(MODEL_GLOSSARY[ov.model]?.label || labelForModel(ov.model) || ov.model);
        return;
      }
      const q = siloId ? `?silo_id=${encodeURIComponent(siloId)}` : "";
      const r = await api.get(`/me/llm-effective/ai_assistant${q}`);
      const id = r.data?.model;
      if (id) { setModelId(id); setModelLabel(MODEL_GLOSSARY[id]?.label || id); }
    } catch { /* fall back to a model-less subtitle */ }
  }, [siloId]);

  const fetchEstimate = useCallback(async () => {
    if (!showSiloControls || !siloId) return;
    try {
      const r = await api.get(`/silos/${siloId}/buddy/context-estimate`);
      setEstimate(r.data);
    } catch { /* cost hint is best-effort */ }
  }, [showSiloControls, siloId]);

  // Initial fetch + live refresh when any AI Assistant in this silo changes the
  // shared surface model (the "family changes together" behavior).
  useEffect(() => {
    fetchEffective();
    fetchEstimate();
    const onChanged = () => { fetchEffective(); fetchEstimate(); };
    window.addEventListener("ai-buddy-model-changed", onChanged);
    return () => window.removeEventListener("ai-buddy-model-changed", onChanged);
  }, [fetchEffective, fetchEstimate]);

  const onPickerSaved = () => {
    window.dispatchEvent(new CustomEvent("ai-buddy-model-changed"));
  };

  // Load the remembered File-mode selection for this silo.
  useEffect(() => {
    if (!siloId) { setSelectedFileIds([]); setSelectedFileMeta(null); return; }
    try {
      const raw = localStorage.getItem(`mysilo_buddy_files_${siloId}`);
      if (raw) { const p = JSON.parse(raw); setSelectedFileIds(p.ids || []); setSelectedFileMeta(p.meta || null); }
      else { setSelectedFileIds([]); setSelectedFileMeta(null); }
    } catch { setSelectedFileIds([]); setSelectedFileMeta(null); }
  }, [siloId]);

  const onFilesSaved = (ids, meta) => {
    setSelectedFileIds(ids);
    setSelectedFileMeta(meta);
    // Feb 10, 2026 — Confirming a file selection flips the Files toggle
    // ON. Clearing the selection (empty ids) flips it back off. Combined
    // with the illegal-state guard, this means a user can never end up
    // with `filesOn=true` and `selectedFileIds=[]`.
    const nowOn = Array.isArray(ids) && ids.length > 0;
    setFilesOn(nowOn);
    // Auto-correct (Randy pt-master-assistant-interlocks): "If files is
    // on, force pop LLM on." When the user just SAVED files while LLM
    // was Off, flip LLM back to Training so the assistant treats the
    // files as reference for the AI — the state the user almost
    // certainly meant.
    if (nowOn && llmMode === "off") {
      setLlmMode("training");
    }
    try { localStorage.setItem(`mysilo_buddy_files_${siloId}`, JSON.stringify({ ids, meta })); } catch { /* */ }
  };

  // Per-bubble voice (reuses the public silo's voice engine). Only the main
  // Workbench assistant (showSiloControls + a silo slug) gets the listen lanes.
  // The chosen voice is the user's PROFILE default (shared with Public Q&A via
  // /me/preferred-voice + the mysilo_preferred_voice cache) so a pick here sticks
  // everywhere until changed again.
  // Jun 2026 (Randy) — the assistant's spoken voice defaults to the CREATOR's
  // per-silo setting (silo.voice_id, default Onyx). A pick here is session-only
  // and reverts to that silo setting on reload.
  const [answerVoice, setAnswerVoiceState] = useState("onyx");
  const setAnswerVoice = (v) => setAnswerVoiceState(v);
  const [listenAllOpen, setListenAllOpen] = useState(false);
  // Jul 2026 (Randy) — carousel modality for the listen options (matches the
  // Public Q&A): show ONE at a time. Order left→right: Free (idx0, hidden behind
  // the left caret) · Premium/forest (idx1, DEFAULT centered).
  const [buddyLaneIndex, setBuddyLaneIndex] = useState(1);
  const buddyLaneTouchX = useRef(null);
  // Jun 2026 (Randy) — UNIVERSAL two-voice cache coverage (matches the public Q&A).
  // One probe per turn (question in questionVoice, answer in answerVoice); every
  // listen button + the per-answer pills read green/cost from THIS single source so
  // they always agree. "Push Cached Convo" is now just a manual "finish the audio"
  // backup that fills whatever this coverage says is still missing.
  const [coverage, setCoverage] = useState({ cached: [], missingChars: 0, allCached: false });
  // Jun 2026 (Randy) — the bookmark currently loaded into this assistant, with its
  // SAVED 7-day audio status. When it's complete+alive the premium pill is bright-
  // green ON ITS OWN (no "Push Cached Convo" needed) and plays the saved audio free
  // — matching the Public Q&A. While it's loaded, listening to more parts auto-saves
  // them into the bookmark behind the scenes (it fills toward complete).
  const [loadedBookmark, setLoadedBookmark] = useState(null); // { id, complete, live }
  const autoSaveRef = useRef(null);
  const listenPillRef = useRef(null);               // the "Listen to whole conversation" pill (scroll target)
  // Full-page composer (Randy): a draggable "green line" above the question box
  // lets the user resize it up/down (public-Q&A feel). Only the answer pane
  // scrolls; the box stays locked at the bottom.
  const [inputHeight, setInputHeight] = useState(56);
  const dragRef = useRef({ y: 0, h: 0, on: false });
  useEffect(() => {
    if (!showSiloControls || !siloId) return;
    let cancel = false;
    api.get(`/silos/${siloId}`)
      .then((r) => {
        if (cancel) return;
        const v = r.data?.voice_id;
        if (v && VOICE_OPTIONS.some((o) => o.id === v)) setAnswerVoiceState(v);
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [showSiloControls, siloId]);
  const voiceEnabled = !!(showSiloControls && siloSlug);
  // Jun 2026 (Randy) — track playback position so the conversation scrubber
  // (dots) can advance, mirroring the Public Q&A control. No screen-hijack here:
  // the AI Assistant keeps its own jump-to-top/bottom buttons.
  const [playPos, setPlayPos] = useState({ msgIdx: -1, frac: 0 });
  const onScrollTick = useCallback((messageIndex, f0, toFrac, durationMs) => {
    if (typeof messageIndex === "number") setPlayPos({ msgIdx: messageIndex, frac: toFrac });
  }, []);
  const voice = useVoiceQA({
    slug: siloSlug,
    ask: async () => null,
    getVoiceId: () => answerVoice,
    voiceReady: true,
    onScrollTick,
    onSelfNoFunding: () => toast.error("Add an OpenAI key or fund a voucher in your dashboard's Operations tab to use premium voice."),
    onGuardBlocked: () => toast.error("Premium voice is blocked — check your LLM key or voucher in Operations."),
  });

  // Clear ONE bubble (this reply), not the whole conversation.
  const clearOneBubble = (id) => setMessages((prev) => prev.filter((x) => x.id !== id));
  // Per-answer "Send to work bench" → flips to "Jump to workbench" once sent.
  const [sentToWB, setSentToWB] = useState(() => new Set());
  // Whole-conversation "All to workbench" (in the Listen panel) → flips to Jump.
  const [wholeWBSent, setWholeWBSent] = useState(false);
  useEffect(() => { setWholeWBSent(false); }, [messages.length]);
  // Trash-can delete: { id, qId, question, answer } of the pair pending confirm.
  const [confirmDel, setConfirmDel] = useState(null);
  const doDeleteTurn = async () => {
    const d = confirmDel;
    if (!d) return;
    try {
      await onDeleteTurn?.({
        session_id: sessionId || undefined,
        bookmark_id: loadedBookmark?.id || undefined,
        question: d.question || undefined,
        answer: d.answer,
      });
      setMessages((prev) => prev.filter((x) => x.id !== d.id && (d.qId ? x.id !== d.qId : true)));
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete. Try again.");
    }
    setConfirmDel(null);
  };

  // Mild location memory: remember whether this primary feature was expanded
  // (same session) so it stays open on return — matches the "your typed data
  // stays" pattern. (Randy, Jun 2026: we intentionally do NOT scroll back to it
  // anymore — every tab starts at the top.)
  useEffect(() => {
    if (!showSiloControls) return;
    try { localStorage.setItem(`buddyopen:${surface}`, open ? "1" : "0"); } catch { /* */ }
  }, [open, showSiloControls, surface]);

  // Load prior history when panel opens for the first time
  useEffect(() => {
    if (!open || !sessionId || historyLoaded) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/buddy/chat/${encodeURIComponent(sessionId)}`);
        if (cancel) return;
        setMessages(r.data?.messages || []);
      } catch { /* silent — fresh session is fine */ }
      finally { if (!cancel) setHistoryLoaded(true); }
    })();
    return () => { cancel = true; };
  }, [open, sessionId, historyLoaded]);

  // Listen for external "seed" events — e.g. the YouTube Tool sending a transcript
  // into this assistant's input. We don't auto-send; we just open the panel and
  // pre-fill the input so the user can phrase their actual question.
  useEffect(() => {
    const onSeed = (e) => {
      if (!e?.detail || e.detail.surface !== surface) return;
      try {
        const seeded = sessionStorage.getItem(seedAssistantStorageKey(surface)) || "";
        if (!seeded) return;
        sessionStorage.removeItem(seedAssistantStorageKey(surface));
        setOpen(true);
        setInput((prev) => (prev ? `${prev}\n\n${seeded}` : seeded));
      } catch { /* ignore */ }
    };
    window.addEventListener("buddy-seed", onSeed);
    // Also pick up a seed that happened BEFORE this component mounted (e.g.
    // YouTube Tool fired the event and the user only just opened the AI Assistant).
    try {
      const pending = sessionStorage.getItem(seedAssistantStorageKey(surface));
      if (pending) {
        sessionStorage.removeItem(seedAssistantStorageKey(surface));
        setOpen(true);
        setInput((prev) => (prev ? `${prev}\n\n${pending}` : pending));
      }
    } catch { /* ignore */ }
    return () => window.removeEventListener("buddy-seed", onSeed);
  }, [surface]);

  // Auto-scroll to latest message — EXCEPT right after loading a bookmark/recall,
  // where Randy wants the conversation to open at the TOP (start), not the bottom.
  const justLoadedRef = useRef(false);
  useEffect(() => {
    if (!scrollRef.current) return;
    if (justLoadedRef.current) {
      justLoadedRef.current = false;
      scrollRef.current.scrollTop = 0;
      try { window.scrollTo({ top: 0 }); } catch { /* */ }
    } else {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    if (assistantLocked) { openGate(_assistantSku); return; }
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    // Continuing the chat diverges from the loaded bookmark snapshot — stop treating
    // it as "the bookmark" (and drop the auto-green) until it's re-saved/re-cached.
    setLoadedBookmark(null);
    // Optimistic render
    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: "user",
      text,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((m) => [...m, optimistic]);
    setInput("");
    try {
      const r = await api.post("/buddy/chat", {
        session_id: sessionId || undefined,
        message: text,
        silo_id: siloId || undefined,
        silo_context: siloContext,
        // Feb 10, 2026 — send when both Silo AND Files are on so the
        // backend can trigger combined-compare mode (files come back in
        // as their own bucket, silo material excludes them).
        silo_file_ids: (filesOn && selectedFileIds.length > 0) ? selectedFileIds : (siloContext === "file" ? selectedFileIds : undefined),
        // Feb 10, 2026 — LLM mode drives grounding + strict reference-only.
        // Only meaningful on the master assistant; backend ignores on other
        // mounts.
        llm_mode: llmMode,
        surface: sessionKey,
        answer_style_preset: answerStyle?.preset || undefined,
        answer_style_custom: answerStyle?.custom || undefined,
        // Iter160p (Randy pt-master-assistant-session-picker) — session-only
        // model pick. `overrideAsAskBody("ai_assistant")` returns `{}` when
        // nothing is picked so the backend cascade proceeds normally.
        ...overrideAsAskBody("ai_assistant"),
      });
      const newSid = r.data.session_id;
      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        setStoredSessionId(sessionKey, newSid);
      }
      setMessages((m) => {
        const without = m.filter((x) => x.id !== optimistic.id);
        return [...without, r.data.user_message, r.data.assistant_message];
      });
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      toast.error(e.response?.data?.detail || "Assistant is unreachable right now.");
    } finally {
      setSending(false);
    }
  };

  // Feb 2026 (Randy) — Enter NEVER submits on the assistant either. Pure
  // newline behavior mirrors the public Q&A composer: paragraph breaks are
  // preserved, send is exclusively via the button.
  const onKeyDown = () => {};

  const clearChat = () => {
    // Jun 2026 (Randy) — "Clear" is a RESET, not a delete. It clears the screen
    // and starts a fresh session; the previous conversation stays in the database
    // (still in your history / Recall). It is NOT removed from the server.
    setMessages([]);
    setStoredSessionId(sessionKey, "");
    setSessionId("");
    setHistoryLoaded(false);
    setLoadedBookmark(null);
    toast.success("Started a fresh chat.");
  };

  // Site-header "Clear" button (full-page assistant) → clear this conversation.
  useEffect(() => {
    if (clearRequest) clearChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearRequest]);

  // Drag the "green line" above the question box to resize it.
  const onInputDragDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { y: e.clientY, h: inputHeight, on: true };
    e.preventDefault();
  };
  const onInputDragMove = (e) => {
    if (!dragRef.current.on) return;
    const dy = e.clientY - dragRef.current.y;   // drag UP (dy<0) → taller
    // Randy (Jun 2026): only allow growing the box UPWARD past the default
    // single-line height (56px) — never shrink it smaller than normal.
    setInputHeight(Math.max(56, Math.min(400, dragRef.current.h - dy)));
  };
  const onInputDragUp = (e) => {
    if (!dragRef.current.on) return;
    dragRef.current.on = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied.");
    } catch { toast.error("Clipboard blocked."); }
  };

  const renderedMessages = useMemo(() => messages.filter((m) => m.role !== "system"), [messages]);

  // Jun 2026 (Randy) — "Listen to whole conversation" in the Workbench assistant.
  // A FAITHFUL CLONE of the public Q&A control: a collapsible pill that expands
  // into the same curved "stamp" lanes. Two lanes apply here (the creator's own
  // assistant): "You pay" (premium, own key) + "Free" (device). Tapping a lane
  // while playing PAUSES/RESUMES (never restarts).
  const _avIdx = VOICE_OPTIONS.findIndex((o) => o.id === answerVoice);
  const questionVoice = VOICE_OPTIONS[((_avIdx < 0 ? 0 : _avIdx) + 1) % VOICE_OPTIONS.length].id;
  const convoPaused = voice.state === "paused";
  // Jun 2026 (Randy) — port of the Public Q&A scrubber + "begin vs latest" popup.
  // One dot per question→answer pair. The popup ONLY fires from the top premium
  // "Listen to whole conversation" button when there are 2+ parts — never from the
  // per-answer pills or the free device lane (matches Public Q&A exactly).
  const [playChoiceOpen, setPlayChoiceOpen] = useState(false);
  const pendingPlayModeRef = useRef("self");
  const lastModeRef = useRef("self");
  const allTurnsScrub = useMemo(
    () => renderedMessages
      .map((m, i) => ({ role: m.role === "user" ? "user" : "assistant", text: (m.text || "").trim(), idx: i }))
      .filter((t) => t.text),
    [renderedMessages],
  );
  // Group each question with the answer beneath it (Randy: "hey Siri, places to
  // eat near me" + its reply = one part), same grouping as the public page.
  const qaSegments = useMemo(() => {
    const segs = [];
    let q = null;
    renderedMessages.forEach((m, i) => {
      const txt = (m.text || "").trim();
      if (!txt) return;
      if (m.role === "user") q = { qPos: i, qText: txt };
      else { segs.push({ qPos: q ? q.qPos : i, aPos: i, label: (q ? q.qText : txt).slice(0, 48) }); q = null; }
    });
    return segs;
  }, [renderedMessages]);
  // Flat scrubber stops (Randy, Sep 2026): one dot per question, one (larger)
  // per answer. Nothing inside an answer.
  const scrubStops = useMemo(
    () =>
      buildScrubStops(
        qaSegments.map((s) => ({
          qMsgIdx: s.qPos,
          qText: renderedMessages[s.qPos]?.text || "",
          aMsgIdx: s.aPos,
          aText: renderedMessages[s.aPos]?.text || "",
        })),
      ),
    [qaSegments, renderedMessages],
  );
  // SCOPE: null = whole conversation. While ONE exchange is playing (a per-answer
  // Premium/Free lane), the drawer narrows to that pair's two dots so a long
  // question can be skipped. Snaps back when that playback ends. (Randy, Sep 2026)
  const [scrubScope, setScrubScope] = useState(null);
  const visibleStops = useMemo(
    () => (scrubScope ? scrubStops.filter((s) => s.segIdx === scrubScope.segIdx) : scrubStops),
    [scrubScope, scrubStops],
  );
  const scrubCurrentStop = stopIndexFor(visibleStops, playPos.msgIdx);
  useEffect(() => {
    if (!scrubScope) return;
    // Another audio source taking over (incl. the whole-conversation read) or
    // playback finishing → back to the full picture.
    if (voice.playKey !== scrubScope.key || voice.state === "idle") setScrubScope(null);
  }, [scrubScope, voice.playKey, voice.state]);
  // The ONE playback entry point for both pipelines: `scope` null reads the
  // conversation onward from that stop; a scope reads only that exchange.
  const playStop = useCallback((stops, stopIdx, mode, scope) => {
    const st = stops[Math.max(0, stopIdx)];
    if (!st) return;
    const m = mode || lastModeRef.current || "self";
    lastModeRef.current = m;
    const endPos = scope ? (qaSegments[st.segIdx]?.aPos ?? st.msgIdx) : Infinity;
    const sliced = allTurnsScrub.filter((t) => t.idx >= st.msgIdx && t.idx <= endPos);
    if (!sliced.length) return;
    setScrubScope(scope || null);
    const key = scope ? scope.key : m === "device" ? "all:device" : "scrub";
    if (m === "device") {
      voice.speakDevice(sliced.map((t) => ({ text: t.text, pitch: t.role === "user" ? 1.18 : 0.92, idx: t.idx })), { key });
      return;
    }
    voice.speakConversation(sliced, { answerVoice, questionVoice, mode: m, key });
  }, [qaSegments, allTurnsScrub, answerVoice, questionVoice, voice]);
  const startWhole = (mode) => {
    lastModeRef.current = mode;
    if (qaSegments.length >= 2) { pendingPlayModeRef.current = mode; setPlayChoiceOpen(true); return; }
    playStop(scrubStops, 0, mode, null);
  };
  // Is the audio playing right now the thing the scrubber is showing?
  const scrubOwnsAudio = scrubScope
    ? voice.playKey === scrubScope.key
    : voice.playKey === "scrub" || (typeof voice.playKey === "string" && voice.playKey.startsWith("all:"));
  const scrubPlaying = voice.state === "speaking" && scrubOwnsAudio;
  // Auto-detect what's already in the 24h cache (cost-free), once per real content/
  // voice change while idle, plus once right after playback finishes. Loop-safe:
  // stable signature + guard ref, narrow deps (NOT the whole voice object).
  const probeSig = useMemo(
    () => allTurnsScrub.map((t) => `${t.idx}:${t.role}:${t.text.length}`).join("|") + `~${answerVoice}/${questionVoice}`,
    [allTurnsScrub, answerVoice, questionVoice],
  );
  const lastProbeSigRef = useRef("");
  const prevVoiceStateRef = useRef("idle");
  const reprobeCoverage = useCallback(async () => {
    const cached = [];
    let missingChars = 0;
    for (const turn of allTurnsScrub) {
      const v = turn.role === "user" ? questionVoice : answerVoice;
      let hit = false;
      try { hit = await voice.probeCached(turn.text, v); } catch { hit = false; }
      if (hit) cached.push(turn.idx);
      else missingChars += turn.text.replace(/\s+/g, " ").trim().length;
    }
    lastProbeSigRef.current = probeSig;
    setCoverage({ cached, missingChars, allCached: allTurnsScrub.length > 0 && cached.length === allTurnsScrub.length });
  }, [allTurnsScrub, answerVoice, questionVoice, voice.probeCached, probeSig]);
  useEffect(() => {
    if (!voiceEnabled || !allTurnsScrub.length) {
      lastProbeSigRef.current = "";
      setCoverage((c) => (!c.cached.length && !c.missingChars && !c.allCached ? c : { cached: [], missingChars: 0, allCached: false }));
      return;
    }
    // Jun 2026 (Randy) — a COMPLETE, still-alive loaded bookmark already holds the
    // whole conversation's audio in its 7-day store. Mark EVERY turn covered so the
    // per-answer "Saved" tags AND the whole-conversation button all show green and
    // play free — don't probe the (long-expired) 24h cache, which made them look
    // grey/"nothing cached" even though the audio is saved.
    if (loadedBookmark?.id && loadedBookmark.complete && loadedBookmark.live) {
      lastProbeSigRef.current = probeSig;
      const allIdx = allTurnsScrub.map((t) => t.idx);
      setCoverage((c) => (c.allCached && c.cached.length === allIdx.length ? c : { cached: allIdx, missingChars: 0, allCached: true }));
      return;
    }
    if (voice.state !== "idle") { prevVoiceStateRef.current = voice.state; return; }
    const justFinished = prevVoiceStateRef.current !== "idle";
    prevVoiceStateRef.current = "idle";
    if (probeSig === lastProbeSigRef.current && !justFinished) return;
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) reprobeCoverage(); }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [probeSig, voiceEnabled, voice.state, allTurnsScrub.length, reprobeCoverage, loadedBookmark?.id, loadedBookmark?.complete, loadedBookmark?.live]);
  // Jun 2026 (Randy) — if the open conversation isn't explicitly loaded as a
  // bookmark (e.g. the browser RESTORED it on a fresh landing), still recognize it:
  // match its opening question to a saved bookmark and adopt that bookmark's audio
  // so the per-answer pills + whole-conversation button show cached/green. One
  // lookup per distinct conversation.
  const matchedKeyRef = useRef("");
  useEffect(() => {
    if (!siloId || loadedBookmark?.id) return;
    const firstUser = (messages.find((m) => m.role === "user")?.text || "").trim();
    if (!firstUser || messages.length < 2) return;
    const key = firstUser.slice(0, 80);
    if (matchedKeyRef.current === key) return;
    matchedKeyRef.current = key;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post(`/silos/${siloId}/match-bookmark`, { first_question: firstUser });
        const m = r.data?.match;
        if (!cancelled && m?.id && m.has_audio) {
          const live = (() => { try { return !!m.audio_expires_at && new Date(m.audio_expires_at).getTime() > Date.now(); } catch { return false; } })();
          setLoadedBookmark({ id: m.id, complete: !!m.audio_complete && live, live });
        }
      } catch { /* */ }
    })();
    return () => { cancelled = true; };
  }, [messages, siloId, loadedBookmark?.id]);
  const coverageCents = coverage.missingChars * 0.0015;
  const coverageCostLabel = coverageCents < 1 ? `${coverageCents.toFixed(1)}¢` : `${Math.round(coverageCents)}¢`;
  const lastAllTapRef = useRef({});
  const allKey = (mode) => `all:${mode}`;
  const allActive = (mode) => (voice.state === "speaking" || voice.state === "paused") && voice.playKey === allKey(mode);
  const startConvo = (mode, key) => {
    const turns = renderedMessages
      .filter((m) => (m.text || "").trim())
      .map((m, i) => ({ role: m.role === "user" ? "user" : "assistant", text: m.text, idx: m.id || i }));
    if (!turns.length) return;
    if (mode === "device") {
      // FREE lane → gesture-safe device-voice queue (fixes iOS "no sound"),
      // questions a touch higher, answers a touch lower.
      voice.speakDevice(turns.map((t) => ({ text: t.text, pitch: t.role === "user" ? 1.18 : 0.92, idx: t.idx })), { key });
      return;
    }
    voice.speakConversation(turns, { answerVoice, questionVoice, mode, key });
  };
  const listenAll = (mode) => {
    const key = allKey(mode);
    if (allActive(mode)) {
      // FREE (device) → single tap STOPS reliably (iOS pause is flaky); tap again
      // replays from the top. PREMIUM → single tap pause/resume, double-tap restart.
      if (mode === "device") { voice.stopSpeaking(); return; }
      const now = Date.now();
      if (now - (lastAllTapRef.current[mode] || 0) < 350) {
        lastAllTapRef.current[mode] = 0;
        voice.stopSpeaking();
        startConvo(mode, key);
        return;
      }
      lastAllTapRef.current[mode] = now;
      voice.pauseOrResume();
      return;
    }
    startConvo(mode, key);
  };

  // ── Part 2: Push Cached Convo (now a "finish the audio" backup) ────────────
  const assistantMsgs = renderedMessages.filter((m) => m.role === "assistant" && (m.text || "").trim());
  // Jun 2026 (Randy) — the loaded bookmark's saved 7-day audio is "ready" (bright-
  // green, free) the moment it's complete + alive. This drives the premium pill
  // automatically. Green everywhere = STATE, never a one-off press.
  const bookmarkAudioReady = !!(loadedBookmark?.id && loadedBookmark.complete && loadedBookmark.live);
  const bmAudioActive = (voice.state === "speaking" || voice.state === "paused") && voice.playKey === "all:bookmark";
  // Auto-green: the whole thread is free to play the instant the 24h cache holds
  // every turn (or a complete saved bookmark is loaded) — no manual step.
  const premiumGreen = coverage.allCached || bookmarkAudioReady;
  // Play the loaded bookmark's SAVED 7-day audio for free (no key, no charge — it
  // was already paid for). Streams the stored chunks straight through Web Audio.
  const playBookmarkAudio = async () => {
    if (bmAudioActive) { voice.pauseOrResume(); return; }
    if (!loadedBookmark?.id) return;
    try {
      const r = await api.get(`/bookmarks/${loadedBookmark.id}/audio`);
      const b64 = (r.data?.chunks || []).map((c) => c.audio_base64).filter(Boolean);
      if (!b64.length) {
        setLoadedBookmark((p) => (p ? { ...p, complete: false } : p));
        toast("That saved audio expired — press Push Cached Convo to rebuild it.", { icon: "↪" });
        return;
      }
      voice.playPrefetched(b64, { key: "all:bookmark" });
      try { api.post(`/s/${siloSlug}/voice/log-free-replay`); } catch { /* */ }
    } catch { toast.error("Couldn't play the saved audio."); }
  };
  const jumpTop = () => { if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: "smooth" }); };
  const jumpBottom = () => { if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); };

  // ===== "Bookmark this conversation" (Randy, Jun 2026) =====================
  const [bookmarkModal, setBookmarkModal] = useState(null);   // { section, title } while naming
  const [bookmarkSaving, setBookmarkSaving] = useState(false);
  const [bmAudioGap, setBmAudioGap] = useState(null);         // { bmId, segs, missCount, cents }
  const [bmAudioFilling, setBmAudioFilling] = useState(false);

  // Report the live conversation up so the Tools / Bookmarks tabs can use it.
  useEffect(() => { onMessagesChange?.(renderedMessages); }, [renderedMessages, onMessagesChange]);

  // Tools tab → "Bookmark this conversation" opens the name-it modal.
  useEffect(() => {
    if (bookmarkRequest) {
      if (!renderedMessages.some((m) => (m.text || "").trim())) { toast("Ask something first — there's nothing to bookmark.", { icon: "↪" }); return; }
      setBookmarkModal({ section: "Saved conversations", title: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmarkRequest]);

  // Tap a saved bookmark → replay its turns into this assistant (display + continue).
  useEffect(() => {
    if (!loadRequest?.nonce) return;
    justLoadedRef.current = true;   // open at the TOP, not auto-scroll to bottom
    const turns = (loadRequest.turns || []).map((m, i) => ({
      id: `bm-${loadRequest.nonce}-${i}`,
      role: m.role === "ai" || m.role === "assistant" ? "assistant" : "user",
      text: m.text || "",
    }));
    setMessages(turns);
    setOpen(true);
    setHistoryLoaded(true);     // these are display turns, not the server session's
    // Recall (Jun 2026, Randy) — adopt the real server session so further turns
    // append to the SAME conversation. Bookmarks pass no session_id → display-only.
    if (loadRequest.session_id) {
      setSessionId(loadRequest.session_id);
      setStoredSessionId(sessionKey, loadRequest.session_id);
    }
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* */ }
    // Jun 2026 (Randy) — OPEN-TIME "complete the audio?" CTA. Fires ONLY when the
    // opened bookmark has PARTIAL audio (has_audio + not yet complete + still alive).
    // No audio at all → no popup. Accept = fill the gaps (→ light-green/complete);
    // decline = keep partial.
    const bmk = loadRequest.bookmark;
    const live = (() => { try { return !!bmk?.audio_expires_at && new Date(bmk.audio_expires_at).getTime() > Date.now(); } catch { return false; } })();
    // Track the loaded bookmark so the premium pill can auto-green from its SAVED
    // 7-day audio (A) and so further listening auto-saves into it (B).
    setLoadedBookmark(bmk?.id ? { id: bmk.id, complete: !!bmk.audio_complete && live, live } : null);
    if (bmk?.id && bmk.has_audio && !bmk.audio_complete && live) {
      (async () => {
        const built = turns
          .filter((m) => (m.text || "").trim())
          .map((m, i) => ({ seg_idx: i, voice: m.role === "user" ? questionVoice : answerVoice, text: m.text, chunks: chunkForSpeech(m.text) }));
        if (!built.length) return;
        try {
          // Check the bookmark's OWN 7-day store (NOT the 24h cache, which has long
          // since expired). Self-heals audio_complete — a fully-listened bookmark
          // no longer nags to re-cache parts it already has saved. (Randy bug)
          const cov = await api.post(`/bookmarks/${bmk.id}/audio-coverage`, {
            segments: built.map((s) => ({ seg_idx: s.seg_idx, chunk_count: s.chunks.length })),
          });
          if (cov.data?.audio_complete) {
            setLoadedBookmark((p) => (p && p.id === bmk.id ? { ...p, complete: true, live: true } : p));
            return; // already fully saved → no popup
          }
          const missSet = new Set(cov.data?.missing_seg_idxs || []);
          const segs = built.map((s) => ({ ...s, cached: !missSet.has(s.seg_idx) }));
          const missSegs = segs.filter((s) => !s.cached);
          if (missSegs.length) {
            const cents = missSegs.reduce((n, s) => n + s.text.replace(/\s+/g, " ").trim().length * 0.0015, 0);
            setBmAudioGap({ bmId: bmk.id, segs, missCount: missSegs.length, cents });
          }
        } catch { /* coverage failed → don't nag */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRequest?.nonce]);

  // (B) Auto-save further listening into the ALREADY-loaded bookmark. Each time the
  // 24h coverage gains a newly-saved part, quietly MERGE the two-voice plan into the
  // bookmark's 7-day store so it fills toward complete — and the premium pill greens
  // on its own. Debounced; scoped to the loaded bookmark (cleared when the chat
  // diverges via a new send).
  useEffect(() => {
    if (!loadedBookmark?.id || loadedBookmark.complete) return;
    if (!coverage.cached.length) return;
    clearTimeout(autoSaveRef.current);
    const bmId = loadedBookmark.id;
    autoSaveRef.current = setTimeout(async () => {
      try {
        const segs = buildTwoVoiceSegments();
        if (!segs.length) return;
        const res = await attachBookmarkAudio(bmId, segs);
        if (res) {
          setLoadedBookmark((p) => (p && p.id === bmId ? { ...p, complete: !!res.audio_complete, live: !!res.has_audio } : p));
          if (res.audio_complete) { setBmAudioGap(null); onBookmarkSaved?.(); }
        }
      } catch { /* best-effort background save */ }
    }, 1800);
    return () => clearTimeout(autoSaveRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverage.cached.length, loadedBookmark?.id, loadedBookmark?.complete]);

  // Build the ordered TWO-VOICE chunk plan exactly as playback reads it (questions
  // in one voice, answers in another) — the single saved-audio scheme for both the
  // 24h cache and the bookmark's 7-day store.
  const buildTwoVoiceSegments = () => renderedMessages
    .filter((m) => (m.text || "").trim())
    .map((m, i) => ({
      seg_idx: i,
      voice: m.role === "user" ? questionVoice : answerVoice,
      text: m.text,
      chunks: chunkForSpeech(m.text),
    }));
  const attachBookmarkAudio = async (bmId, segs) => {
    try {
      const r = await api.post(`/bookmarks/${bmId}/attach-audio`, {
        segments: segs.map((s) => ({ seg_idx: s.seg_idx, voice: s.voice, chunks: s.chunks })),
      });
      return r.data;
    } catch { return null; /* audio is best-effort; the bookmark itself is already saved */ }
  };
  const saveAssistantBookmark = async ({ section, title }) => {
    const turns = renderedMessages.filter((m) => (m.text || "").trim())
      .map((m) => ({ role: m.role === "user" ? "user" : "ai", text: m.text }));
    if (!turns.length) return;
    setBookmarkSaving(true);
    try {
      const r = await api.post("/bookmarks", {
        slug: siloSlug, section_name: section || "Saved conversations", file_title: title || "Untitled", messages: turns, origin: "assistant",
      });
      const bm = r.data;
      setBookmarkModal(null);
      // Single scheme: the TWO-VOICE plan (questions in one voice, answers in
      // another) — exactly what every listen button caches.
      const segs = buildTwoVoiceSegments();
      if (!segs.length) { toast.success("Bookmarked."); onBookmarkSaved?.(); return; }
      const probed = await Promise.all(segs.map(async (s) => ({ ...s, cached: await voice.probeCached(s.text, s.voice) })));
      const cachedCount = probed.filter((p) => p.cached).length;
      if (cachedCount === 0) {
        // The reader skipped audio entirely → no popup, no audio (greyed play icon).
        toast.success("Bookmarked.");
        onBookmarkSaved?.();
      } else if (cachedCount === probed.length) {
        await attachBookmarkAudio(bm.id, probed);
        toast.success("Bookmarked with full audio.");
        onBookmarkSaved?.();
      } else {
        // Some pieces missing → offer to gather the rest (same CTA), then attach.
        const miss = probed.filter((p) => !p.cached);
        const cents = miss.reduce((n, s) => n + s.text.replace(/\s+/g, " ").trim().length * 0.0015, 0);
        // Attach what we already have so partial audio survives if they bail.
        await attachBookmarkAudio(bm.id, probed);
        onBookmarkSaved?.();
        setBmAudioGap({ bmId: bm.id, segs: probed, missCount: miss.length, cents });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not save bookmark."));
    } finally { setBookmarkSaving(false); }
  };
  const fillBookmarkAudioGap = async () => {
    if (!bmAudioGap) return;
    setBmAudioFilling(true);
    const miss = bmAudioGap.segs.filter((s) => !s.cached);
    try {
      await Promise.all(miss.map(async (s) => { try { await voice.warmCached(s.text, s.voice); } catch { /* */ } }));
      const res = await attachBookmarkAudio(bmAudioGap.bmId, bmAudioGap.segs);
      // Reflect completion in the LOADED conversation so the per-answer pills AND
      // the whole-conversation button flip to green immediately (Fix B keys off
      // loadedBookmark.complete) — don't leave them stale + still quoting a price.
      if (res) {
        setLoadedBookmark((p) => (p && p.id === bmAudioGap.bmId ? { ...p, complete: !!res.audio_complete, live: !!res.has_audio } : p));
      }
      if (res?.audio_complete) toast.success("Bookmark audio completed — it now plays free when shared.");
      else toast("Saved what we could. Add an OpenAI key/voucher with funds to finish the rest.", { icon: "↪" });
    } finally {
      setBmAudioFilling(false);
      setBmAudioGap(null);
      onBookmarkSaved?.();
    }
  };


  const connTail = showSiloControls && siloContext === "silo"
    ? "reading your whole silo (smart search)"
    : showSiloControls && siloContext === "full"
    ? "reading your full Tier 1"
    : showSiloControls && siloContext === "chunks"
    ? "reading relevant silo snippets"
    : "not connected to your silo";
  const fullSubtitle = `${subtitle}${modelLabel ? ` · ${modelLabel}` : ""} · ${connTail}`;

  const estForMode = estimate
    ? (siloContext === "silo" ? estimate.est_silo_cents : siloContext === "full" ? estimate.est_full_cents : siloContext === "chunks" ? estimate.est_chunks_cents : estimate.est_off_cents)
    : null;

  const smallContextWarn = (siloContext === "full" || siloContext === "silo") && modelId && !modelId.startsWith("gemini");

  return (
    <>
    <div ref={rootRef} className={`relative bg-white overflow-hidden ${hideHeader ? "flex flex-col flex-1 min-h-0" : "border hairline rounded-xl"} ${(hideHeader || compact) ? "" : "shadow-sm"} ${(!hideHeader && showSiloControls) ? "ring-1 ring-[#C16E5A]/35 shadow-[0_12px_44px_-14px_rgba(193,110,90,0.45)]" : ""}`} data-testid={`ai-buddy-${surface}`}>
      {showSiloControls && !hideHeader && (
        <div className="h-[3px] w-full bg-gradient-to-r from-[#C16E5A] via-[#D08A6E] to-[#A89360]" aria-hidden="true" />
      )}
      {!hideHeader && (
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`ai-buddy-toggle-${surface}`}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#FDFCF8]"
      >
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${showSiloControls ? "bg-gradient-to-br from-[#C16E5A] to-[#A85E4A] text-white shadow-[0_0_16px_rgba(193,110,90,0.55)]" : "bg-[#1C1B1A] text-[#FDFCF8]"}`}>
          <Robot size={showSiloControls ? 18 : 16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          {showSiloControls && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#C16E5A] font-semibold leading-none mb-1">Your Assistant</div>
          )}
          <div className={`text-[#1C1B1A] truncate ${showSiloControls ? "text-[15px] font-semibold" : "text-sm font-medium"}`}>{title}</div>
          <div className="text-xs text-[#6B6A68] truncate">
            {fullSubtitle}
          </div>
        </div>
        {renderedMessages.length > 0 && (
          <span className="text-[10px] text-[#6B6A68] tabular-nums">{renderedMessages.length} msg</span>
        )}
        {open ? <CaretUp size={14} className="text-[#6B6A68]" /> : <CaretDown size={14} className="text-[#6B6A68]" />}
      </button>
      )}

      {showSiloControls && siloId && (
        <div
          data-testid="ai-buddy-silo-controls"
          className="py-2.5 border-t hairline bg-[#FBFAF6] shrink-0"
        >
          <div className="w-full max-w-4xl mx-auto px-[9%] sm:px-4 flex flex-col gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Button 1 — LLM Mode (cycles Training → Training+🌐 → Off) */}
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68] mr-1">LLM</span>
            <button
              type="button"
              onClick={() => {
                // Feb 10, 2026 (Randy pt-master-assistant-interlocks) —
                // Cycle: Training → Grounded → Off → Training. All three
                // states are reachable. Auto-corrects are handled by
                // `setLlmModeSafe`: landing on Off with no silo pops silo
                // on so the assistant never has "no source" moment.
                const next = llmMode === "training"
                  ? "training_grounded"
                  : llmMode === "training_grounded"
                    ? "off"
                    : "training";
                setLlmModeSafe(next);
              }}
              data-testid="ai-buddy-llm-mode-btn"
              title={
                llmMode === "training"
                  ? "AI on — the assistant answers from what it learned during training. Tap to add live Google Search."
                  : llmMode === "training_grounded"
                    ? "AI + Live Google Search — knows current events. Adds ~3¢ per grounded question. Tap to turn AI off."
                    : "AI off — the assistant answers ONLY from your silo/files. Tap to switch back to AI."
              }
              className={`px-2.5 py-1 text-[11px] rounded-md border hairline transition-colors inline-flex items-center gap-1 ${
                llmMode === "off"
                  ? "bg-white text-[#6B6A68] border-hairline"
                  : "bg-[#1C1B1A] text-[#FDFCF8] border-[#1C1B1A]"
              }`}
            >
              {llmMode === "training" && (<><span>AI</span><BookOpen size={12} weight="duotone" /></>)}
              {llmMode === "training_grounded" && (<><span>AI</span><Globe size={12} weight="duotone" /></>)}
              {llmMode === "off" && <span>Off</span>}
            </button>

            {/* Button 2 — Silo (independent on/off toggle) */}
            {(() => {
              const btn = (
                <button
                  type="button"
                  onClick={() => {
                    if (siloKnowledgeLocked) { openGate(_siloSku); return; }
                    // Auto-corrects (see setSiloOnSafe): turning silo off
                    // while LLM is off flips LLM back to Training so the
                    // assistant always has at least one knowledge source.
                    setSiloOnSafe(!siloOn);
                  }}
                  data-testid="ai-buddy-silo-toggle"
                  title="Silo knowledge — mixes in your Tier 1 anchor + smart-searched deeper library."
                  className={`px-2.5 py-1 text-[11px] rounded-md border hairline transition-colors ${
                    siloOn
                      ? "bg-[#1C1B1A] text-[#FDFCF8] border-[#1C1B1A]"
                      : "bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]"
                  }`}
                >
                  Silo{siloOn ? " · on" : ""}
                </button>
              );
              return siloKnowledgeLocked ? (
                <LockGate locked lockStyle={lockStyle} onLockedClick={() => openGate(_siloSku)}>{btn}</LockGate>
              ) : (
                <span className="inline-flex">{btn}</span>
              );
            })()}

            {/* Button 3 — Files (independent on/off toggle; opens picker when arming) */}
            {(() => {
              const btn = (
                <button
                  type="button"
                  onClick={() => {
                    if (siloKnowledgeLocked) { openGate(_siloSku); return; }
                    if (filesOn) {
                      // Toggling files off is always allowed — the LLM
                      // stays wherever it was.
                      setFilesOnSafe(false);
                      return;
                    }
                    // Arming — open the picker. filesOn only flips true
                    // after the picker's onConfirm fires with ≥1 file.
                    setFilePickerOpen(true);
                  }}
                  data-testid="ai-buddy-files-toggle"
                  title="Files — pair with specific files from your silo (max 3). When combined with Silo, the assistant compares the files against the rest of the silo."
                  className={`px-2.5 py-1 text-[11px] rounded-md border hairline transition-colors ${
                    filesOn
                      ? "bg-[#1C1B1A] text-[#FDFCF8] border-[#1C1B1A] ai-file-shimmer"
                      : "bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]"
                  }`}
                >
                  Files{filesOn && selectedFileIds.length > 0 ? ` · ${selectedFileIds.length}` : ""}
                </button>
              );
              return siloKnowledgeLocked ? (
                <LockGate locked lockStyle={lockStyle} onLockedClick={() => openGate(_siloSku)}>{btn}</LockGate>
              ) : (
                <span className="inline-flex">{btn}</span>
              );
            })()}
            {combineQA && (
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                data-testid="ai-buddy-share-btn"
                title="Share this conversation"
                className="ml-auto inline-flex items-center gap-0.5 px-2 py-1 text-[10px] font-semibold rounded-md border border-[#27AE60]/55 bg-[#27AE60]/10 text-[#1f8a4c] hover:bg-[#27AE60]/20 transition-colors"
              >
                <ShareNetwork size={12} weight="duotone" /> Share
              </button>
            )}
          </div>
          {siloOn && filesOn && (
            <div className="text-[10px] text-[#7A5C20] flex items-start gap-1 bg-[#FAF2DA] border hairline rounded px-2 py-1" data-testid="ai-buddy-compare-hint">
              <Sparkle size={12} weight="duotone" className="shrink-0 mt-px" />
              <span>Comparing selected files against the rest of your silo — the assistant will surface agreements, contradictions, and gaps.</span>
            </div>
          )}
          {smallContextWarn && (
            <div className="text-[10px] text-[#C16E5A] flex items-start gap-1" data-testid="ai-buddy-context-warning">
              <Warning size={12} weight="fill" className="shrink-0 mt-px" />
              <span>
                {modelLabel} has a smaller context window — your full Tier 1
                {estimate ? ` (~${Math.round((estimate.tier1_chars || 0) / 1000)}k chars)` : ""} may be truncated.
                Switch to a Gemini model for full coverage.
              </span>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] text-[#6B6A68] tabular-nums flex items-center gap-2" data-testid="ai-buddy-cost-hint">
              {filesOn ? (
                selectedFileIds.length > 0 ? (
                  <button type="button" onClick={() => setFilePickerOpen(true)} data-testid="ai-buddy-file-edit" className="hover:text-[#1C1B1A] hover:underline">
                    {selectedFileIds.length} file{selectedFileIds.length > 1 ? "s" : ""} loaded{selectedFileMeta?.chars ? ` · ~${Math.max(1, Math.round(selectedFileMeta.chars / 1000))}k chars` : ""}{selectedFileMeta?.mode === "rag" ? " · smart-searched" : ""} · Edit
                  </button>
                ) : (
                  <button type="button" onClick={() => setFilePickerOpen(true)} data-testid="ai-buddy-file-edit" className="hover:text-[#1C1B1A] hover:underline">No files selected — tap to choose</button>
                )
              ) : estimate ? (
                <>
                  <span>≈ {fmtCents(estForMode)} / message</span>
                  {siloOn && (
                    <span>· Tier 1 ≈ {Math.round((estimate.tier1_chars || 0) / 1000)}k chars + smart-searched deeper tiers</span>
                  )}
                </>
              ) : (
                <span>&nbsp;</span>
              )}
              {llmMode === "training_grounded" && (
                <span className="text-[#7A5C20]" data-testid="ai-buddy-grounding-fee-hint">
                  · +~3¢ Google Search fee when grounded
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                data-testid="ai-buddy-model-picker-btn"
                className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md border hairline bg-white text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1"
              >
                <SlidersHorizontal size={11} weight="bold" /> Model{modelLabel ? `: ${modelLabel}` : ""}
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {open && (
        <div className={`border-t hairline ${hideHeader ? "flex flex-col flex-1 min-h-0" : ""}`} data-testid={`ai-buddy-panel-${surface}`}>
          <div className={hideHeader ? "flex flex-col flex-1 min-h-0" : "relative"}>
            {voiceEnabled && (hideHeader || renderedMessages.some((m) => (m.text || "").trim())) && (
              <div
                ref={listenPillRef}
                data-testid="buddy-listen-conversation"
                className="py-2 border-b hairline bg-[#FBFAF6] shrink-0"
              >
                <div className="w-full max-w-4xl mx-auto px-[9%] sm:px-4 flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  data-testid="buddy-listen-lanes-toggle"
                  onClick={() => setListenAllOpen((v) => !v)}
                  disabled={!renderedMessages.some((m) => (m.text || "").trim())}
                  aria-expanded={listenAllOpen}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] font-semibold leading-none px-2.5 py-0.5 rounded-full bg-white border hairline shadow-sm cursor-pointer hover:shadow transition-shadow text-[#6B6A68] disabled:opacity-50 disabled:cursor-default disabled:hover:shadow-sm"
                >
                  <Waveform size={11} weight="duotone" />
                  Listen to whole conversation
                </button>
                <AnimatePresence initial={false}>
                  {listenAllOpen && (
                    <motion.div
                      key="buddy-listen-lanes"
                      initial={{ opacity: 0, scale: 0.3, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.3, y: -10 }}
                      transition={{ duration: 0.26, ease: "easeOut" }}
                      style={{ transformOrigin: "50% 0%", touchAction: "pan-y" }}
                      className="relative w-full flex items-center justify-center gap-1.5"
                      data-testid="buddy-listen-lanes"
                      onTouchStart={(e) => { buddyLaneTouchX.current = e.touches?.[0]?.clientX ?? null; }}
                      onTouchEnd={(e) => {
                        if (buddyLaneTouchX.current == null) return;
                        const endX = e.changedTouches?.[0]?.clientX ?? buddyLaneTouchX.current;
                        const dx = endX - buddyLaneTouchX.current;
                        buddyLaneTouchX.current = null;
                        if (Math.abs(dx) < 40) return;
                        setBuddyLaneIndex((i) => { const c = Math.min(1, Math.max(0, i)); return Math.min(1, Math.max(0, c + (dx > 0 ? -1 : 1))); });
                      }}
                    >
                      {/* Up/down jump buttons (Randy): far-LEFT of the drawer,
                          centered vertically; square + black. They hide with the
                          drawer when it's minimized. */}
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5" data-testid="buddy-jump-arrows">
                        <button type="button" onClick={jumpTop} data-testid="buddy-jump-top" aria-label="Jump to top"
                          className="w-9 h-9 rounded-lg bg-[#1C1B1A] text-white shadow-sm flex items-center justify-center hover:bg-[#3A4A3E] transition-colors">
                          <CaretUp size={16} weight="bold" />
                        </button>
                        <button type="button" onClick={jumpBottom} data-testid="buddy-jump-bottom" aria-label="Jump to bottom"
                          className="w-9 h-9 rounded-lg bg-[#1C1B1A] text-white shadow-sm flex items-center justify-center hover:bg-[#3A4A3E] transition-colors">
                          <CaretDown size={16} weight="bold" />
                        </button>
                      </div>
                      {(() => {
                        const buddyLaneNodes = [
                          // index 0 — FREE device voice (LEFT, hidden behind the caret)
                          <CurvedListenButton
                            key="free"
                            id="buddy-all-free"
                            testid="buddy-listen-all-free"
                            topLabel="Free"
                            bottomLabel="Voice"
                            straightLabel="Free"
                            color="#1C1B1A"
                            onClick={() => { lastModeRef.current = "device"; listenAll("device"); }}
                            icon={allActive("device") ? <Stop size={22} weight="fill" /> : <SpeakerSimpleHigh size={22} weight="fill" />}
                          />,
                          // index 1 — PREMIUM / forest (DEFAULT, centered)
                          <CurvedListenButton
                            key="premium"
                            id="buddy-all-premium"
                            testid="buddy-listen-all-premium"
                            topLabel={premiumGreen ? "" : `Costs (~${coverageCostLabel})`}
                            bottomLabel={bookmarkAudioReady ? "Pre-paid" : "Premium"}
                            straightLabel={premiumGreen ? "You already paid" : "You pay"}
                            color={premiumGreen ? "#27AE60" : "#4A5D4E"}
                            warming={voice.warming && lastModeRef.current === "self"}
                            onClick={() => {
                              if (bookmarkAudioReady) { playBookmarkAudio(); return; }
                              lastModeRef.current = "self";
                              if (voice.state === "speaking" || voice.state === "paused") { voice.pauseOrResume(); return; }
                              startWhole("self");
                            }}
                            icon={(() => {
                              const active = bookmarkAudioReady ? bmAudioActive : (scrubPlaying || voice.playKey === "scrub");
                              if (active) return convoPaused ? <Play size={22} weight="fill" /> : <Pause size={22} weight="fill" />;
                              return <SpeakerHigh size={22} weight="fill" />;
                            })()}
                          />,
                        ];
                        const laneCount = buddyLaneNodes.length;
                        const curLane = Math.min(Math.max(0, buddyLaneIndex), laneCount - 1);
                        const goLane = (dir) => setBuddyLaneIndex((i) => Math.min(laneCount - 1, Math.max(0, Math.min(Math.max(0, i), laneCount - 1) + dir)));
                        return (
                          <>
                            <button
                              type="button"
                              data-testid="buddy-listen-lane-prev"
                              aria-label="Previous listen option"
                              onClick={() => goLane(-1)}
                              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-[#1C1B1A] transition-transform hover:scale-110 active:scale-95"
                              style={{ visibility: curLane > 0 ? "visible" : "hidden" }}
                            >
                              <CaretLeft size={20} weight="bold" />
                            </button>
                            <div className="flex items-center justify-center" style={{ minWidth: 96 }}>
                              {buddyLaneNodes[curLane]}
                            </div>
                            <button
                              type="button"
                              data-testid="buddy-listen-lane-next"
                              aria-label="Next listen option"
                              onClick={() => goLane(1)}
                              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-[#1C1B1A] transition-transform hover:scale-110 active:scale-95"
                              style={{ visibility: curLane < laneCount - 1 ? "visible" : "hidden" }}
                            >
                              <CaretRight size={20} weight="bold" />
                            </button>
                          </>
                        );
                      })()}
                      {/* Workbench + voice controls — pushed to the far RIGHT (desktop)
                          to line up with the Share button + Model selector above. On
                          mobile they stay in the centered flow. "Voice" stays under the
                          voice select. */}
                      <div className="flex flex-col items-center justify-center sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2" style={{ width: 96 }} data-testid="buddy-workbench-voice">
                        {onSendAllToWorkbench && (
                          wholeWBSent && onJumpToWorkbench ? (
                            <button
                              type="button"
                              data-testid="buddy-send-all-jump"
                              onClick={onJumpToWorkbench}
                              className="w-full mb-1 text-[9px] uppercase tracking-[0.06em] font-semibold rounded-md bg-[#4A5D4E] text-white px-1.5 py-1 leading-tight hover:bg-[#3A4A3E] transition-colors"
                            >
                              Jump to workbench
                            </button>
                          ) : (
                            <button
                              type="button"
                              data-testid="buddy-send-all-workbench"
                              title="Send the whole conversation to your Workbench"
                              onClick={async () => {
                                try {
                                  const all = renderedMessages
                                    .filter((x) => (x.text || "").trim())
                                    .map((x) => ({ role: x.role === "user" ? "user" : "assistant", text: x.text }));
                                  if (!all.length) return;
                                  await onSendAllToWorkbench(all);
                                  setWholeWBSent(true);
                                } catch { /* toast handled by caller */ }
                              }}
                              className="w-full mb-1 text-[9px] uppercase tracking-[0.06em] font-semibold rounded-md border hairline bg-[#FDFCF8] text-[#4A5D4E] px-1.5 py-1 leading-tight hover:border-[#4A5D4E] transition-colors"
                            >
                              All to workbench
                            </button>
                          )
                        )}
                        <select
                          value={answerVoice}
                          onChange={(e) => setAnswerVoice(e.target.value)}
                          data-testid="buddy-listen-voice-select"
                          className="w-full text-[11px] rounded-md border hairline bg-white px-1.5 py-1 text-[#4A5D4E]"
                        >
                          {VOICE_OPTIONS.map((o) => (
                            <option key={o.id} value={o.id}>{o.label}</option>
                          ))}
                        </select>
                        <div className="text-[12px] font-semibold leading-none mt-1 text-[#1C1B1A]">Voice</div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {listenAllOpen && visibleStops.length >= 1 && (
                  <>
                    {scrubScope && (
                      <div className="w-full flex items-center justify-center gap-2 mt-2" data-testid="buddy-qa-scrubber-scope-chip">
                        <span className="text-xs font-medium text-[#4A4A48]">
                          This exchange · {scrubScope.segIdx + 1} of {qaSegments.length}
                        </span>
                        <button
                          type="button"
                          data-testid="buddy-qa-scrubber-scope-reset"
                          onClick={() => setScrubScope(null)}
                          className="text-xs font-semibold text-[#4A5D4E] underline underline-offset-2 hover:text-[#3A4A3E] transition-colors"
                        >
                          Whole conversation
                        </button>
                      </div>
                    )}
                    <ConversationScrubber
                      stops={visibleStops}
                      currentStop={scrubCurrentStop}
                      frac={playPos.frac || 0}
                      playing={scrubPlaying}
                      onPlayPause={() => {
                        if (scrubOwnsAudio && (voice.state === "speaking" || voice.state === "paused")) { voice.pauseOrResume(); return; }
                        playStop(visibleStops, scrubCurrentStop >= 0 ? scrubCurrentStop : 0, lastModeRef.current, scrubScope);
                      }}
                      onPrev={() => playStop(visibleStops, Math.max(0, (scrubCurrentStop < 0 ? 0 : scrubCurrentStop) - 1), lastModeRef.current, scrubScope)}
                      onNext={() => playStop(visibleStops, Math.min(visibleStops.length - 1, (scrubCurrentStop < 0 ? 0 : scrubCurrentStop) + 1), lastModeRef.current, scrubScope)}
                      onJump={(i) => playStop(visibleStops, i, lastModeRef.current, scrubScope)}
                      color="#4A5D4E"
                      testidPrefix="buddy-qa-scrubber"
                    />
                  </>
                )}
                </div>
              </div>
            )}
            <div ref={scrollRef} className={`overflow-y-auto ${hideHeader ? "flex-1 min-h-0 flex flex-col" : "resize-y px-4 py-3 space-y-3 bg-[#FDFCF8]"}`} style={hideHeader ? undefined : { resize: "vertical", minHeight: fullHeight ? "52vh" : "180px", height: fullHeight ? "52vh" : "340px", maxHeight: "1200px" }}>
            <div className={hideHeader ? `buddy-content-surface w-full max-w-4xl mx-auto flex-1 bg-[#FDFCF8] px-[9%] sm:px-4 py-3 flex flex-col gap-3 ${renderedMessages.length === 0 ? "justify-center" : "justify-end"}` : "contents"}>
            {renderedMessages.length === 0 && (
              emptyState ? (
                <div className="py-6 flex items-center justify-center min-h-full" data-testid="ai-buddy-empty-state">{typeof emptyState === "function" ? emptyState(siloContext) : emptyState}</div>
              ) : (
                <div className="text-xs text-[#6B6A68] italic py-6 text-center">
                  {emptyHint}
                </div>
              )
            )}
            {renderedMessages.map((m, i) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`buddy-msg-${m.role}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#1C1B1A] text-[#FDFCF8]" : "bg-white border hairline text-[#1C1B1A]"}`}>
                  {m.role === "assistant" && showSiloControls ? (
                    <div className="text-sm leading-relaxed whitespace-normal" data-testid="buddy-msg-rich">
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" className="text-[#4A5D4E] underline underline-offset-2 hover:text-[#1C1B1A] break-words" />
                          ),
                          p: ({ node, ...props }) => <p {...props} className="mb-2 last:mb-0" />,
                          // Jun 2026 (ASSISTANT_MARKDOWN_MODES.md) — Randy asked
                          // four times for "1. Language" and got an unnumbered
                          // list every time. The model complied on the FIRST
                          // try: `1.` left the backend intact and died here.
                          // ReactMarkdown parses it into an <ol>, and Tailwind's
                          // preflight resets `ol, ul, menu { list-style: none }`
                          // globally — so the marker was built and then stripped
                          // by the stylesheet, leaving bare lines. The assistant
                          // cannot see its own rendered output, so it apologised
                          // and re-sent identical text, forever.
                          //
                          // Bullets hid the bug: `flatten_markdown` rewrites
                          // `- item` to a literal `• item` BEFORE this renderer
                          // sees it, so bullets are plain text and survive.
                          // Numbered lists stay real Markdown and got eaten.
                          // PublicSilo (.prose-theo) and FamilyAsk already
                          // restore markers; this surface never did.
                          ol: ({ node, ordered, ...props }) => <ol {...props} className="list-decimal pl-5 mb-2 last:mb-0 space-y-0.5" />,
                          ul: ({ node, ordered, ...props }) => <ul {...props} className="list-disc pl-5 mb-2 last:mb-0 space-y-0.5" />,
                          li: ({ node, ordered, ...props }) => <li {...props} className="pl-1" />,
                          // Rules are lifted in world/grounded mode, so real
                          // headings and emphasis can arrive now and need somewhere to land.
                          h1: ({ node, ...props }) => <h1 {...props} className="text-base font-medium mt-3 mb-1.5 first:mt-0" />,
                          h2: ({ node, ...props }) => <h2 {...props} className="text-base font-medium mt-3 mb-1.5 first:mt-0" />,
                          h3: ({ node, ...props }) => <h3 {...props} className="text-sm font-medium mt-2.5 mb-1 first:mt-0" />,
                          strong: ({ node, ...props }) => <strong {...props} className="font-semibold" />,
                          blockquote: ({ node, ...props }) => <blockquote {...props} className="border-l-2 border-[#C9C6BC] pl-3 my-2 text-[#4A4947]" />,
                          // Wrap code/command blocks INSIDE the bubble — markdown's
                          // default <pre> uses white-space:pre and runs off-screen
                          // (Randy's long-line glitch on "Draft Command:" output).
                          pre: ({ node, ...props }) => <pre {...props} className="whitespace-pre-wrap break-words overflow-x-auto bg-black/[0.04] rounded-md p-2 my-2 text-[13px]" />,
                          code: ({ node, inline, ...props }) => (
                            inline
                              ? <code {...props} className="px-1 py-0.5 rounded bg-black/[0.06] text-[13px] break-words" />
                              : <code {...props} className="block whitespace-pre-wrap break-words" />
                          ),
                        }}
                      >
                        {linkifyUrls(m.text)}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    m.text
                  )}
                  {m.role === "assistant" && m.grounding?.used && (
                    <div className="mt-2 pt-2 border-t hairline" data-testid="buddy-msg-grounding">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#7A5C20] mb-1 flex items-center gap-1">
                        <span aria-hidden="true">🌐</span> Grounded via Google Search
                      </div>
                      {Array.isArray(m.grounding.citations) && m.grounding.citations.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {m.grounding.citations.slice(0, 8).map((c, ci) => (
                            <a
                              key={ci}
                              href={c.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`buddy-msg-citation-${ci}`}
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FAF2DA] border hairline text-[#7A5C20] hover:bg-[#F5E4B3] truncate max-w-[220px]"
                              title={c.uri}
                            >
                              {c.title || c.uri}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {m.role === "assistant" && (
                    <div className="mt-2 pt-2 border-t hairline flex flex-wrap gap-1.5">
                      {onAppendToStaging && (
                        <button onClick={() => onAppendToStaging(m.text)} data-testid="buddy-append-staging"
                          className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md border hairline bg-[#FDFCF8] text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1">
                          <ArrowUp size={11} weight="bold" /> Append to staging
                        </button>
                      )}
                      {onCommitToSilo && (
                        <button onClick={() => onCommitToSilo(m.text)} data-testid="buddy-commit-silo"
                          className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1">
                          <FloppyDisk size={11} weight="duotone" /> Commit to silo
                        </button>
                      )}
                      {onPasteToInstructions && (
                        <button onClick={() => onPasteToInstructions(m.text)} data-testid="buddy-paste-instructions"
                          className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md border hairline bg-[#FDFCF8] text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1">
                          <ClipboardText size={11} weight="bold" /> Paste to instructions
                        </button>
                      )}
                      <button onClick={() => copyToClipboard(m.text)} data-testid="buddy-copy"
                        className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md border hairline bg-[#FDFCF8] text-[#6B6A68] hover:border-[#1C1B1A] hover:text-[#1C1B1A]">
                        Copy
                      </button>
                      {onSendToWorkbench && (
                        sentToWB.has(m.id) && onJumpToWorkbench ? (
                          <button
                            onClick={onJumpToWorkbench}
                            data-testid="buddy-jump-workbench"
                            className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1">
                            <ArrowUp size={11} weight="bold" /> Jump to workbench
                          </button>
                        ) : (
                          <button
                            onClick={async () => {
                              try {
                                await onSendToWorkbench({
                                  answer: m.text,
                                  question: (i > 0 && renderedMessages[i - 1]?.role === "user") ? renderedMessages[i - 1].text : null,
                                });
                                setSentToWB((s) => new Set(s).add(m.id));
                              } catch { /* toast handled by caller */ }
                            }}
                            data-testid="buddy-send-workbench"
                            className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md border hairline bg-[#FDFCF8] text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1">
                            <ArrowUp size={11} weight="bold" /> Send to work bench
                          </button>
                        )
                      )}
                      {onCommitToSilo && (
                        <button onClick={() => clearOneBubble(m.id)} data-testid="buddy-clear-inline"
                          className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md border hairline bg-[#FDFCF8] text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-1">
                          <Trash size={11} weight="duotone" /> Clear
                        </button>
                      )}
                    </div>
                  )}
                  {m.role === "assistant" && voiceEnabled && (
                    <div className="mt-1 flex justify-center items-end gap-2 overflow-hidden">
                      <AiBuddyVoiceLanes
                        voice={voice}
                        text={m.text}
                        msgKey={m.id}
                        question={combineQA && i > 0 && renderedMessages[i - 1].role === "user" ? renderedMessages[i - 1].text : null}
                        answerVoice={answerVoice}
                        questionVoice={questionVoice}
                        setAnswerVoice={setAnswerVoice}
                        aIdx={i}
                        onScope={(key, mode) => {
                          lastModeRef.current = mode;
                          setScrubScope({ segIdx: qaSegments.findIndex((s) => s.aPos === i), key });
                        }}
                        qIdx={combineQA && i > 0 && renderedMessages[i - 1].role === "user" ? i - 1 : "buddy-q"}
                        cached={coverage.cached.includes(i) && (!(combineQA && i > 0 && renderedMessages[i - 1].role === "user") || coverage.cached.includes(i - 1))}
                      />
                      {onDeleteTurn && (
                        <button
                          type="button"
                          data-testid="buddy-delete-turn"
                          title="Delete this answer and its question"
                          aria-label="Delete this answer and its question"
                          onClick={() => setConfirmDel({
                            id: m.id,
                            qId: (i > 0 && renderedMessages[i - 1]?.role === "user") ? renderedMessages[i - 1].id : null,
                            question: (i > 0 && renderedMessages[i - 1]?.role === "user") ? renderedMessages[i - 1].text : null,
                            answer: m.text,
                          })}
                          className="w-9 h-9 rounded-lg border hairline bg-white text-[#C16E5A] hover:bg-[#C16E5A]/10 hover:border-[#C16E5A] flex items-center justify-center shrink-0 transition-colors"
                        >
                          <Trash size={16} weight="duotone" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="buddy-thinking">
                <div className="bg-white border hairline text-[#6B6A68] text-sm rounded-lg px-3 py-2 italic">
                  Thinking…
                </div>
              </div>
            )}
            </div>
          </div>
            {!hideHeader && <ResizeHint title="Drag bottom-right to resize the chat area" />}
          </div>

          {hideHeader ? (
            <div className="shrink-0 bg-white pb-[calc(0.75rem+env(safe-area-inset-bottom))]" data-testid="buddy-composer">
              <div className="w-full max-w-4xl mx-auto px-[9%] sm:px-4">
                {/* Draggable "green line" — drag up/down to resize the question box */}
                <div
                  role="separator"
                  aria-label="Drag to resize the question box"
                  data-testid="buddy-composer-resize"
                  onPointerDown={onInputDragDown}
                  onPointerMove={onInputDragMove}
                  onPointerUp={onInputDragUp}
                  onPointerCancel={onInputDragUp}
                  className="h-3 flex items-center justify-center cursor-ns-resize touch-none select-none border-t-2 border-[#4A5D4E]/70 hover:border-[#4A5D4E]"
                >
                  <DotsSixVertical size={14} weight="bold" className="text-[#4A5D4E]/70" style={{ transform: "rotate(90deg)" }} />
                </div>
                <div className="relative">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={inputPlaceholder}
                    data-testid={`buddy-input-${surface}`}
                    style={{ height: `${inputHeight}px` }}
                    className="w-full resize-none bg-[#FDFCF8] border hairline rounded-3xl pl-5 pr-16 py-3 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] whitespace-pre-wrap break-words"
                  />
                  {/* Creator Pro lock OUTSIDE the AI-credit lock so the upgrade
                      card always fires first; positioning stays on the wrapper. */}
                  <div className="absolute right-2.5 bottom-2">
                    <LockGate locked={assistantLocked} lockStyle={lockStyle} onLockedClick={() => openGate(_assistantSku)}>
                      <LLMGuardWrap surface="ai_assistant" siloId={siloId}>
                    <button
                      type="button"
                      onClick={send}
                      disabled={!input.trim() || sending}
                      data-testid={`buddy-send-${surface}`}
                      aria-label="Send"
                      className="w-11 h-11 rounded-full bg-[#4A5D4E] text-white flex items-center justify-center hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <PaperPlaneTilt size={18} weight="fill" />
                    </button>
                      </LLMGuardWrap>
                    </LockGate>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-t hairline px-3 py-2 flex items-end gap-2 bg-white">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={inputPlaceholder}
                data-testid={`buddy-input-${surface}`}
                className="flex-1 resize-none bg-[#FDFCF8] border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] min-h-[36px] max-h-[140px]"
              />
              <LockGate locked={assistantLocked} lockStyle={lockStyle} onLockedClick={() => openGate(_assistantSku)}>
                <LLMGuardWrap surface="ai_assistant" siloId={siloId}>
                <button
                  type="button"
                  onClick={send}
                  disabled={!input.trim() || sending}
                  data-testid={`buddy-send-${surface}`}
                  className="px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1 text-xs"
                >
                  <PaperPlaneTilt size={14} weight="duotone" /> Send
                </button>
                </LLMGuardWrap>
              </LockGate>
              {renderedMessages.length > 0 && (
                <button
                  type="button"
                  onClick={clearChat}
                  title="Clear conversation"
                  data-testid={`buddy-clear-${surface}`}
                  className="p-2 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE]"
                >
                  <Trash size={14} weight="duotone" />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
    {pickerOpen && siloId && (
      // Iter160p (Randy pt-master-assistant-session-picker, Feb 2026) —
      // swapped the persistent AiAssistantModelPicker (PATCHed the silo's
      // ai_assistant override silo-wide) for the SessionLlmPickerModal
      // pattern already used on Public Q&A + YTRE. Pick lives in
      // sessionStorage (same-tab-only), evaporates on refresh/logout, and
      // does NOT reconfigure the silo's default assistant. Perfect for
      // one-off experimentation with different models without leaking a
      // configuration decision silo-wide or across collaborators.
      <SessionLlmPickerModal
        surface="ai_assistant"
        siloId={siloId}
        onClose={() => setPickerOpen(false)}
        onSaved={onPickerSaved}
      />
    )}
    <BuddyFilePicker
      siloId={siloId}
      open={filePickerOpen && !!siloId}
      initialSelected={selectedFileIds}
      onClose={() => setFilePickerOpen(false)}
      onSave={onFilesSaved}
    />
    {shareOpen && (
      <ShareConversationSheet
        slug={siloSlug}
        siloName={siloName}
        messages={renderedMessages}
        bookmarkId={loadedBookmark?.id || null}
        answerVoice={answerVoice}
        onClose={() => setShareOpen(false)}
        onSaved={(id) => { if (id && !loadedBookmark?.id) setLoadedBookmark({ id, complete: false, live: false }); onBookmarkSaved?.(); }}
      />
    )}
    {bookmarkModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="buddy-bookmark-modal">
        <div className="modal-safe bg-white rounded-xl shadow-xl border hairline max-w-sm w-full p-5">
          <div className="text-[15px] font-semibold text-[#1C1B1A] flex items-center gap-2"><FloppyDisk size={16} weight="duotone" /> Bookmark this conversation</div>
          <p className="mt-1 text-xs text-[#6B6A68]">Saves the whole conversation. Any cached voice audio rides along and is kept for 7 days.</p>
          <label className="block mt-3 text-[10px] uppercase tracking-[0.16em] text-[#6B6A68]">Section</label>
          <input data-testid="buddy-bookmark-section" value={bookmarkModal.section}
            onChange={(e) => setBookmarkModal((s) => ({ ...s, section: e.target.value }))}
            className="mt-1 w-full text-sm rounded-md border hairline bg-[#FDFCF8] px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
          <label className="block mt-3 text-[10px] uppercase tracking-[0.16em] text-[#6B6A68]">Title</label>
          <input data-testid="buddy-bookmark-title" value={bookmarkModal.title} autoFocus placeholder="Name this conversation…"
            onChange={(e) => setBookmarkModal((s) => ({ ...s, title: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter" && !bookmarkSaving) saveAssistantBookmark(bookmarkModal); }}
            className="mt-1 w-full text-sm rounded-md border hairline bg-[#FDFCF8] px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setBookmarkModal(null)} data-testid="buddy-bookmark-cancel"
              className="px-3 py-1.5 rounded-md border hairline text-sm text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
            <button type="button" onClick={() => saveAssistantBookmark(bookmarkModal)} disabled={bookmarkSaving || !bookmarkModal.title.trim()} data-testid="buddy-bookmark-save"
              className="px-3 py-1.5 rounded-md bg-[#1C1B1A] text-white text-sm font-semibold hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
              {bookmarkSaving ? "Saving…" : "Save bookmark"}
            </button>
          </div>
        </div>
      </div>
    )}
    {confirmDel && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 px-4" data-testid="buddy-delete-turn-overlay" onClick={() => setConfirmDel(null)}>
        <div className="modal-safe bg-white rounded-xl shadow-xl border hairline max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
          <div className="text-[15px] font-semibold text-[#1C1B1A] flex items-center gap-2"><Trash size={16} weight="duotone" /> Delete this answer?</div>
          <p className="mt-1.5 text-sm text-[#6B6A68]">This removes this answer{confirmDel.question ? " and its question" : ""} from this conversation and from your saved history. This can't be undone.</p>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setConfirmDel(null)} data-testid="buddy-delete-turn-cancel"
              className="px-3 py-1.5 rounded-md border hairline text-sm text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
            <button type="button" onClick={doDeleteTurn} data-testid="buddy-delete-turn-confirm"
              className="px-3 py-1.5 rounded-md bg-[#C16E5A] text-white text-sm font-semibold hover:bg-[#A85948] flex items-center gap-1.5">
              <Trash size={13} weight="bold" /> Delete
            </button>
          </div>
        </div>
      </div>
    )}
    {bmAudioGap && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="buddy-bookmark-audio-gap">
        <style>{`@keyframes gapslide { 0% { left: -35% } 100% { left: 100% } }`}</style>
        <div className="modal-safe bg-white rounded-xl shadow-xl border hairline max-w-sm w-full p-5 relative">
          <button type="button" onClick={() => { setBmAudioFilling(false); setBmAudioGap(null); }} data-testid="buddy-bookmark-audio-close"
            className="absolute top-3 right-3 text-[#9b9a96] hover:text-[#1C1B1A] p-1" title="Close"><X size={16} /></button>
          <div className="text-[15px] font-semibold text-[#1C1B1A] pr-6">Save the rest of the audio?</div>
          <p className="mt-1.5 text-sm text-[#6B6A68]">{bmAudioGap.missCount} {bmAudioGap.missCount === 1 ? "part hasn't" : "parts haven't"} been voiced yet. I can record {bmAudioGap.missCount === 1 ? "it" : "them"} so this bookmark has the complete audio.</p>
          {bmAudioFilling ? (
            <div className="mt-4" data-testid="buddy-bookmark-audio-progress">
              <div className="h-2 w-full rounded-full bg-[#EDEDE7] overflow-hidden relative">
                <div className="h-full w-[35%] bg-[#27AE60] rounded-full absolute top-0" style={{ animation: "gapslide 1.1s ease-in-out infinite" }} />
              </div>
              <div className="mt-2 text-xs text-[#6B6A68] text-center">Recording the missing parts…</div>
            </div>
          ) : (
            <div className="mt-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setBmAudioGap(null)} data-testid="buddy-bookmark-audio-skip"
                className="px-3 py-1.5 rounded-md border hairline text-sm text-[#6B6A68] hover:text-[#1C1B1A]">Not now</button>
              <button type="button" onClick={fillBookmarkAudioGap} data-testid="buddy-bookmark-audio-fill"
                className="px-3 py-1.5 rounded-md bg-[#27AE60] text-white text-sm font-semibold hover:bg-[#229954] flex items-center gap-1.5">
                Record the rest
                <span className="text-[11px] bg-white/20 rounded px-1.5 py-0.5">~{bmAudioGap.cents < 1 ? bmAudioGap.cents.toFixed(1) : Math.round(bmAudioGap.cents)}¢</span>
              </button>
            </div>
          )}
        </div>
      </div>
    )}
    {playChoiceOpen && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" data-testid="buddy-play-choice-overlay" onClick={() => setPlayChoiceOpen(false)}>
        <div className="modal-safe bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" data-testid="buddy-play-choice-popup" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-[#1C1B1A] mb-1">Where should it start?</h3>
          <p className="text-sm text-[#6B6A68] mb-5">You already have a few exchanges here — pick up from the latest, or replay the whole thread.</p>
          <div className="space-y-2">
            <button type="button" data-testid="buddy-play-choice-beginning" onClick={() => { setPlayChoiceOpen(false); playStop(scrubStops, 0, pendingPlayModeRef.current, null); }} className="w-full py-3 rounded-full bg-[#4A5D4E] text-white text-sm font-semibold hover:bg-[#3A4A3E] transition-colors">Play from the beginning</button>
            <button type="button" data-testid="buddy-play-choice-latest" onClick={() => { setPlayChoiceOpen(false); playStop(scrubStops, Math.max(0, scrubStops.findIndex((s) => s.segIdx === qaSegments.length - 1)), pendingPlayModeRef.current, null); }} className="w-full py-3 rounded-full border border-[#4A5D4E] text-[#4A5D4E] text-sm font-semibold hover:bg-[#F0F1EE] transition-colors">Start from the latest question</button>
            <button type="button" data-testid="buddy-play-choice-cancel" onClick={() => setPlayChoiceOpen(false)} className="w-full py-2 text-xs text-[#9A9892] hover:text-[#6B6A68] transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
